document.addEventListener("DOMContentLoaded", function () {
  const wrapper = document.getElementById("giftcard-checker");
  if (!wrapper) return;

  const FUNCTION_URL =
    "https://sportivegiftcardchecker.netlify.app/.netlify/functions/giftcardcheck";

  const form = document.getElementById("giftcard-checker-form");
  const codeInput = document.getElementById("giftcard-code");
  const passwordInput = document.getElementById("giftcard-password");
  const messageBox = document.getElementById("giftcard-checker-message");

  const scanButton = document.getElementById("giftcard-scan-button");
  const scannerContainer = document.getElementById("giftcard-scanner");

  let html5QrCode = null;
  let scanning = false;

  function showMessage(text, type) {
    messageBox.textContent = text;
    messageBox.className = "giftcard-checker-message " + (type || "");
  }

  // Extract Shopify gift card code from QR text like:
  // "shopify-giftcard-v1-2BEG9G4ADHH7DD48"
  function extractCodeFromPayload(text) {
    if (!text) return "";
    text = text.trim();
    const prefix = "shopify-giftcard-v1-";

    if (text.startsWith(prefix)) {
      return text.slice(prefix.length).trim();
    }

    const idx = text.lastIndexOf("v1-");
    if (idx !== -1) {
      return text.slice(idx + 3).trim();
    }

    // Last fallback: return original, cleaned
    return text;
  }

  // ---- QR SCANNING ----
  async function startScan() {
    if (scanning) return;

    if (!window.Html5Qrcode) {
      console.error("Html5Qrcode is not defined");
      showMessage("QR scanner library not loaded.", "error");
      return;
    }

    // Ensure container is visible
    if (scannerContainer) {
      scannerContainer.style.display = "block";
    }

    if (!html5QrCode) {
      html5QrCode = new Html5Qrcode("giftcard-scanner");
    }

    scanning = true;
    scanButton.textContent = "Stop scanning";

    const config = { fps: 10, qrbox: 250 };

    const onScanSuccess = (decodedText /*, decodedResult */) => {
      console.log("QR decoded:", decodedText);
      const extracted = extractCodeFromPayload(decodedText);
      if (extracted) {
        codeInput.value = extracted;
        showMessage(
          "Code scanned successfully. You can now check or deactivate.",
          "success"
        );
      } else {
        showMessage(
          "QR scanned but could not extract a valid code.",
          "error"
        );
      }
      stopScan();
    };

    const onScanFailure = (errorMessage) => {
      // Called for every failed decode; too noisy to show to user.
      // console.debug("Scan failure:", errorMessage);
    };

    try {
      // Try environment camera first
      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        onScanSuccess,
        onScanFailure
      );
    } catch (err) {
      console.warn(
        "Failed to start environment camera, falling back to user camera:",
        err
      );
      try {
        await html5QrCode.start(
          { facingMode: "user" },
          config,
          onScanSuccess,
          onScanFailure
        );
      } catch (err2) {
        console.error("Failed to start any camera:", err2);
        showMessage(
          "Could not access camera or start QR scanner. Check browser permissions.",
          "error"
        );
        scanning = false;
        scanButton.textContent = "Scan QR code";
        if (scannerContainer) {
          scannerContainer.style.display = "none";
        }
      }
    }
  }

  function stopScan() {
    if (!html5QrCode || !scanning) return;
    html5QrCode
      .stop()
      .then(() => {
        return html5QrCode.clear();
      })
      .then(() => {
        scanning = false;
        scanButton.textContent = "Scan QR code";
        if (scannerContainer) {
          scannerContainer.style.display = "none";
        }
      })
      .catch((err) => {
        console.error("Failed to stop QR scanner:", err);
        scanning = false;
        scanButton.textContent = "Scan QR code";
        if (scannerContainer) {
          scannerContainer.style.display = "none";
        }
      });
  }

  if (scanButton && scannerContainer) {
    scanButton.addEventListener("click", function () {
      if (scanning) {
        stopScan();
      } else {
        startScan();
      }
    });
  }

  // ---- FORM SUBMIT / API CALL ----
  form.addEventListener("submit", async function (event) {
    event.preventDefault();

    const submitter = event.submitter; // which button was clicked
    const action = submitter?.dataset?.action || "check";

    const code = (codeInput.value || "").trim();
    const password = (passwordInput.value || "").trim();

    if (!code) {
      showMessage("Please enter or scan a gift card code.", "error");
      return;
    }

    if (action === "deactivate" && !password) {
      showMessage(
        "Employee password is required to deactivate a card.",
        "error"
      );
      return;
    }

    if (action === "deactivate") {
      const confirmDeactivate = window.confirm(
        "This will permanently deactivate the gift card. Continue?"
      );
      if (!confirmDeactivate) return;
    }

    showMessage(
      action === "check"
        ? "Checking gift card balance..."
        : "Deactivating gift card...",
      "loading"
    );

    try {
      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          code,
          action,
          password: action === "deactivate" ? password : undefined
        })
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        const msg =
          data.error ||
          data.detail ||
          "Something went wrong. Please try again.";
        showMessage(msg, "error");
        return;
      }

      const masked = data.maskedCode
        ? data.maskedCode
        : data.lastCharacters
        ? `**** **** **** ${data.lastCharacters}`
        : "this card";

      if (action === "check") {
        const disabled = data.isDisabled;

        // ✅ If deactivated, ONLY say it's deactivated (no balance)
        if (disabled) {
          showMessage(`${masked} is deactivated.`, "success");
          return;
        }

        const amount = data.balanceAmount;
        const currency = data.currency;

        let text = `${masked} has a remaining balance of ${amount} ${currency}.`;

        if (data.expiresOn) {
          text += ` Expires on: ${data.expiresOn}.`;
        }

        showMessage(text, "success");
      } else if (action === "deactivate") {
        let text = data.message || "Gift card has been deactivated.";
        if (data.disabledAt) {
          text += ` Deactivated at: ${data.disabledAt}.`;
        }
        showMessage(text, "success");

        // optional: clear password after success
        // passwordInput.value = "";
      }
    } catch (err) {
      console.error(err);
      showMessage("Network or server error. Please try again.", "error");
    }
  });

  // For debugging: confirm everything loaded
  console.log(
    "giftcardcheck.js loaded; Html5Qrcode =",
    typeof window.Html5Qrcode
  );
});
